import React, { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import Topbar from '../../components/Bar/Topbar';
import Header from '../../components/Bar/Header';
import Banner from '../../components/Carousel/Banner';
import FeaturesSection from '../../components/Sections/FeaturesSection';
import FactsSection from '../../components/Sections/FactsSection';
import ServiceSection from '../../components/Sections/ServiceSection';
import SubscribeSection from '../../components/Sections/SubscribeSection';
import Footer from '../../components/Footer/Footer';
import ServiceGrid from '../../components/Sections/ServiceGrid';
import MainContainer3 from '../../components/Carousel/MainContainer3';

// ==================== PALETTE WHITE / BEIGE ====================
const colors = {
  bg: '#FDF8F0',
  glass: 'rgba(255, 255, 255, 0.65)',
  border: 'rgba(245, 235, 220, 0.6)',
  accent: '#C69C6D',
  text: '#3B2F2A',
  shadow: '0 12px 40px rgba(107, 90, 70, 0.08)',
};

// ==================== SECTION ANIMÉE AU SCROLL (améliorée) ====================
const AnimatedSection = ({ children, delay = 0.2, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ==================== WIDGET FLOTTANT EN VERRE DÉPOLI ====================
const FloatingWidget = () => {
  const widgetVariants = {
    hidden: { opacity: 0, x: 80, scale: 0.8 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 200, damping: 20, delay: 1.5 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={widgetVariants}
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '12px',
      }}
    >
      {/* Bouton principal – Glassmorphism */}
      <motion.a
        href="https://wa.me/243835593568" // adaptez le numéro
        target="_blank"
        rel="noreferrer"
        whileHover={{ scale: 1.08, y: -2 }}
        whileTap={{ scale: 0.95 }}
        style={{
          background: colors.glass,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          border: `1px solid ${colors.border}`,
          borderRadius: '50px',
          padding: '14px 22px',
          color: colors.text,
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: colors.shadow,
          transition: 'box-shadow 0.3s',
        }}
      >
        <i className="fa-brands fa-whatsapp" style={{ fontSize: '1.3rem', color: '#25D366' }} />
        <span>Échangez avec nous</span>
      </motion.a>

      {/* Bouton secondaire – Retour en haut */}
      <motion.button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        whileHover={{ scale: 1.1, y: -2 }}
        whileTap={{ scale: 0.95 }}
        style={{
          background: colors.glass,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          border: `1px solid ${colors.border}`,
          borderRadius: '50%',
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.accent,
          fontSize: '1.2rem',
          cursor: 'pointer',
          boxShadow: colors.shadow,
        }}
      >
        <i className="fa-solid fa-chevron-up" />
      </motion.button>
    </motion.div>
  );
};

// ==================== COMPOSANT HOME ====================
const Home = () => {
  // Scroll vers le haut à chaque montage
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2, ease: 'easeOut' }}
      style={{
        background: colors.bg,
        minHeight: '100vh',
        position: 'relative',
        overflowX: 'hidden',
      }}
    >
      {/* ---- TOP BAR + HEADER GLASS (importés) ---- */}
     
      <Header />

      {/* ---- BANNIÈRE GLASS (importée) ---- */}
      <Banner />

      {/* ---- SECTIONS ANIMÉES ---- */}
      <AnimatedSection delay={0.2}>
        <FeaturesSection />
      </AnimatedSection>
      <AnimatedSection delay={0.3}>
        <MainContainer3 />
      </AnimatedSection>
      <AnimatedSection delay={0.35}>
        <FactsSection />
      </AnimatedSection>
      <AnimatedSection delay={0.4}>
        <ServiceSection />
      </AnimatedSection>
      <AnimatedSection delay={0.45}>
        <ServiceGrid />
      </AnimatedSection>
      <AnimatedSection delay={0.5}>
        <SubscribeSection />
      </AnimatedSection>

      {/* ---- FOOTER ---- */}
      <Footer />

      {/* ---- WIDGET FLOTTANT ---- */}
      <FloatingWidget />
    </motion.div>
  );
};

export default Home;